import * as THREE from 'three';
import { createNoise3D, type NoiseFunction3D } from 'simplex-noise';
import { FLOORPLAN } from '../gen/config';
import { surfaceHeight } from '../gen/terrain/height';
import type { InteriorManager } from './interiorManager';
import type { TileManager } from './tileManager';

function inside(x: number, y: number, flat: number[]): boolean {
  let c = false;
  const n = flat.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const ax = flat[i * 2];
    const ay = flat[i * 2 + 1];
    const bx = flat[j * 2];
    const by = flat[j * 2 + 1];
    if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) c = !c;
  }
  return c;
}

function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / l2)) : 0;
  return Math.hypot(ax + dx * t - px, ay + dy * t - py);
}

/** Distance to the polygon outline and the nearest point on it. */
function nearestEdge(x: number, y: number, flat: number[]): { d: number; px: number; py: number } {
  let best = { d: Infinity, px: x, py: y };
  const n = flat.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = flat[i * 2];
    const ay = flat[i * 2 + 1];
    const dx = flat[j * 2] - ax;
    const dy = flat[j * 2 + 1] - ay;
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l2)) : 0;
    const px = ax + dx * t;
    const py = ay + dy * t;
    const d = Math.hypot(px - x, py - y);
    if (d < best.d) best = { d, px, py };
  }
  return best;
}

/** Concrete foundation depth below a building's ground floor before it turns to earth (cm). */
const FOUNDATION_DEPTH = 300;

export type SolidKind = 'concrete' | 'dirt' | 'wall';

/** Resolution of the material view; the browser scales it up to the window. */
const VIEW_W = 144;
const VIEW_H = 90;
/** Distance (m) of the sampled shell around the camera: the material "right in front of your eyes". */
const SHELL = 0.6;

function seededNoise(salt: number): NoiseFunction3D {
  let s = 0x9e3779b9 ^ salt;
  return createNoise3D(() => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  });
}

/**
 * Full-screen material view when the free camera is inside solid matter: concrete inside a tower floor
 * with no generated interior, a slab or ceiling plenum, or a low-rise (whose shells are solid); dirt
 * below the ground. The view samples 3D noise on a small shell around the camera in world space, so
 * the grain slides and changes as you move or turn, like pushing through the material. Dirt is lighter
 * toward the side of the view that faces up (toward the surface). Rooms of generated floors, lobbies
 * and open air stay visible.
 */
export class SolidOverlay {
  readonly el: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly label: HTMLSpanElement;
  private readonly img: ImageData;
  private readonly n = [seededNoise(1), seededNoise(2), seededNoise(3), seededNoise(4)];
  private shown: SolidKind | null = null;
  private lastKey = '';

  constructor(
    container: HTMLElement,
    private readonly tiles: TileManager,
    private readonly interiors: InteriorManager,
    private readonly seed: () => number,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'solid-overlay';
    this.canvas = document.createElement('canvas');
    this.canvas.width = VIEW_W;
    this.canvas.height = VIEW_H;
    this.el.appendChild(this.canvas);
    this.label = document.createElement('span');
    this.el.appendChild(this.label);
    container.appendChild(this.el);
    this.ctx = this.canvas.getContext('2d')!;
    this.img = this.ctx.createImageData(VIEW_W, VIEW_H);
  }

  get active(): SolidKind | null {
    return this.shown;
  }

  /** x, y, z: camera in generation space (cm); camera: for the view directions. */
  update(x: number, y: number, z: number, camera: THREE.PerspectiveCamera): void {
    const kind = this.kindAt(x, y, z);
    if (kind !== this.shown) {
      this.shown = kind;
      this.el.style.display = kind ? 'flex' : 'none';
      this.label.textContent = kind === 'dirt' ? 'underground' : kind === 'wall' ? 'inside wall' : 'inside structure';
      this.lastKey = '';
    }
    if (!kind) return;
    // Redraw only when the camera moved or turned.
    const e = camera.matrixWorld.elements;
    const key = `${kind}|${e.map((v) => v.toFixed(3)).join(',')}|${camera.fov}|${camera.aspect.toFixed(3)}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.render(kind, camera);
  }

  private render(kind: SolidKind, camera: THREE.PerspectiveCamera): void {
    const m = camera.matrixWorld.elements;
    const right = [m[0], m[1], m[2]];
    const up = [m[4], m[5], m[6]];
    const fwd = [-m[8], -m[9], -m[10]];
    const px = m[12];
    const py = m[13];
    const pz = m[14];
    const tanY = Math.tan((camera.fov * Math.PI) / 360);
    const tanX = tanY * camera.aspect;
    const [n1, n2, n3, n4] = this.n;
    const d = this.img.data;
    for (let j = 0; j < VIEW_H; j++) {
      const sy = (1 - ((j + 0.5) / VIEW_H) * 2) * tanY;
      for (let i = 0; i < VIEW_W; i++) {
        const sx = (((i + 0.5) / VIEW_W) * 2 - 1) * tanX;
        let dx = fwd[0] + right[0] * sx + up[0] * sy;
        let dy = fwd[1] + right[1] * sx + up[1] * sy;
        let dz = fwd[2] + right[2] * sx + up[2] * sy;
        const l = Math.hypot(dx, dy, dz);
        dx /= l;
        dy /= l;
        dz /= l;
        // Sample point in metres (three space): on the shell around the camera.
        const x = px + dx * SHELL;
        const y = py + dy * SHELL;
        const z = pz + dz * SHELL;
        let r: number;
        let g: number;
        let b: number;
        if (kind === 'wall') {
          // Painted plaster over gypsum: pale, fine paper grain, a few darker flecks, faint trowel swirls.
          let v = 0.66 + 0.04 * n1(x * 5, y * 5, z * 5) + 0.025 * n2(x * 70, y * 70, z * 70);
          v += 0.03 * Math.sin((n3(x * 3, y * 3, z * 3) + x * 0.8 + z * 0.6) * 9);
          if (n4(x * 90, y * 90, z * 90) > 0.82) v -= 0.08;
          r = v;
          g = v * 0.975;
          b = v * 0.93;
        } else if (kind === 'concrete') {
          // Dark cement with fine grain, lighter aggregate and a few pores.
          let v = 0.17 + 0.045 * n1(x * 9, y * 9, z * 9) + 0.03 * n2(x * 38, y * 38, z * 38);
          const agg = n3(x * 22, y * 22, z * 22);
          if (agg > 0.62) v += 0.05 + 0.04 * (agg - 0.62);
          if (n4(x * 55, y * 55, z * 55) > 0.78) v -= 0.07;
          r = v;
          g = v * 0.985;
          b = v * 0.96;
        } else {
          // Brown earth, darker clods, grey pebbles; lighter toward up (the surface).
          const lift = 1 + 0.6 * dy;
          let k = 0.85 + 0.2 * n1(x * 6, y * 6, z * 6) + 0.1 * n2(x * 30, y * 30, z * 30);
          if (n4(x * 12, y * 12, z * 12) > 0.55) k *= 0.8;
          r = 0.25 * k;
          g = 0.175 * k;
          b = 0.11 * k;
          const peb = n3(x * 18, y * 18, z * 18);
          if (peb > 0.68) {
            const s = 0.3 + 0.12 * n2(x * 60, y * 60, z * 60);
            r = s;
            g = s * 0.95;
            b = s * 0.88;
          }
          r *= lift;
          g *= lift;
          b *= lift;
        }
        const o = (j * VIEW_W + i) * 4;
        d[o] = Math.max(0, Math.min(255, r * 255));
        d[o + 1] = Math.max(0, Math.min(255, g * 255));
        d[o + 2] = Math.max(0, Math.min(255, b * 255));
        d[o + 3] = 255;
      }
    }
    this.ctx.putImageData(this.img, 0, 0);
  }

  kindAt(x: number, y: number, z: number): SolidKind | null {
    const tx = Math.round(x / 25000);
    const ty = Math.round(y / 25000);
    let surface: number | null = null;
    const ground = () => (surface ??= surfaceHeight(this.seed(), x, y));
    /** Below a building's ground floor: its plinth/foundation is concrete (earth deeper down). */
    const below = (floorZ: number): SolidKind => (z > floorZ - FOUNDATION_DEPTH || z > ground() ? 'concrete' : 'dirt');
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const t = this.tiles.tiles.get(`${tx + dx},${ty + dy}`);
        if (!t?.buildings) continue;
        for (const b of t.buildings) {
          const floorZ = b.baseZ - 2;
          if (b.kind === 'lowrise') {
            // Row houses: rooms of loaded floors are open, their walls plaster, the rest concrete.
            for (const u of b.units ?? []) {
              if (!inside(x, y, u.poly)) continue;
              const uz = u.baseZ - 2;
              if (z < uz) return below(uz);
              const f = Math.floor((z - uz) / b.floorHeight);
              if (f >= u.floors) continue;
              const hit = this.roomAt(u.id, f, x, y, z);
              return hit === 'room' ? null : hit === 'wall' ? 'wall' : 'concrete';
            }
            continue;
          }
          if (b.kind !== 'tower' || !b.floorOutlines) continue;
          if (z < floorZ) {
            if (inside(x, y, b.footprint)) return below(floorZ);
            continue;
          }
          const f = Math.floor((z - floorZ) / b.floorHeight);
          if (f >= b.floors) continue;
          if (!inside(x, y, b.floorOutlines[f])) continue;
          if (f < b.baseFloorTop) return null; // the lobby hall is open
          const hit = this.roomAt(b.id, f, x, y, z);
          return hit === 'room' ? null : hit === 'wall' ? 'wall' : 'concrete';
        }
      }
    }
    if (z < ground() - 5) return 'dirt';
    return null;
  }

  /** In the clear height of a room of a generated tower floor (for indoor exposure). */
  indoors(x: number, y: number, z: number): boolean {
    const tx = Math.round(x / 25000);
    const ty = Math.round(y / 25000);
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        const t = this.tiles.tiles.get(`${tx + dx},${ty + dy}`);
        for (const b of t?.buildings ?? []) {
          for (const u of b.units ?? []) {
            if (!inside(x, y, u.poly)) continue;
            const f = Math.floor((z - (u.baseZ - 2)) / b.floorHeight);
            if (f >= 0 && f < u.floors) return this.inRoom(u.id, f, x, y, z);
          }
          if (b.kind !== 'tower' || !b.floorOutlines) continue;
          const f = Math.floor((z - (b.baseZ - 2)) / b.floorHeight);
          if (f < b.baseFloorTop || f >= b.floors || !inside(x, y, b.floorOutlines[f])) continue;
          return this.inRoom(b.id, f, x, y, z);
        }
      }
    return false;
  }

  /** Kept for callers that only need a yes/no. */
  inSolid(x: number, y: number, z: number): boolean {
    return this.kindAt(x, y, z) !== null;
  }

  /** In the clear height of a room of a generated floor (not inside one of its walls). */
  private inRoom(buildingId: string, floor: number, x: number, y: number, z: number): boolean {
    return this.roomAt(buildingId, floor, x, y, z) === 'room';
  }

  /**
   * Where the point is on a generated floor: in a room, inside a wall (within a room's wall thickness
   * of its outline, outside door and window openings), or neither (null: slab, plenum, not loaded).
   */
  private roomAt(buildingId: string, floor: number, x: number, y: number, z: number): 'room' | 'wall' | null {
    for (const fl of this.interiors.floors.values()) {
      if (fl.buildingId !== buildingId || fl.floor !== floor) continue;
      const h = z - fl.result.z0;
      for (const r of fl.result.rooms) {
        const roof = r.roof;
        // The open stairwell of a row house runs through the floor: its full floor height is clear.
        const clearTop = r.recipe === 'stairs' ? fl.result.z0 + 400 : fl.result.z0 + roof;
        if (!(h > 2 && z < clearTop - 2) || !inside(x, y, r.poly)) continue;
        const thick = r.wallThickness + 2;
        const edge = nearestEdge(x, y, r.poly);
        if (edge.d >= thick) return 'room';
        // Doorways (full height for open 'noWall' links) and window openings are passable.
        for (const d of fl.result.doors) {
          const top = d.type === 'noWall' ? roof : FLOORPLAN.doorTopHeight;
          if (h < top && Math.hypot(d.x - edge.px, d.y - edge.py) < d.width / 2 + 5) return 'room';
        }
        const w = r.windows;
        for (let k = 0; k + 7 < w.length; k += 8) {
          if (z < w[k + 4] || z > w[k + 5]) continue;
          if (distToSegment(edge.px, edge.py, w[k], w[k + 1], w[k + 2], w[k + 3]) < 5) return 'room';
        }
        return 'wall';
      }
    }
    return null;
  }
}
